import React, { useState, useEffect, useRef, useMemo } from "react"
import styled from "@emotion/styled"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  FormHelperText,
  Alert,
} from "@mui/material"
import {
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { useForm, Controller } from "react-hook-form"
import { addAiAgent, editAiAgent, listAvailableLlmModels } from "services/aiAgent"
import { listAiVoices } from "services/aiVoices"
import { verifyResponse } from "utils/httpRequest"
import { useSnackbar } from "notistack"
import RichTextEditor from "components/common/RichTextEditor"
import useMediaManager from "components/common/MediaManager"
import { getTierPrograms } from "services/tierProgram"
import { getProducts } from "services/products"
import { MEDIA_TYPE_INDEX } from "constants/global"
import {
  mergeLlmCatalog,
  extractLlmCombinedFromResponse,
  extractLlmSourceFromResponse,
} from "utils/llmCatalogMerge"

const StyledAiAgentDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(1120px, calc(100vw - 28px));
    max-width: none;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 8px;
  }

  .MuiDialogContent-root {
    padding: 10px 22px 10px;
  }

  .MuiDialogActions-root {
    padding: 8px 22px 18px;
    gap: 10px;
  }

  .dialog-title-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .dialog-cancel-btn,
  .dialog-submit-btn {
    text-transform: none;
    border-radius: 10px;
    min-height: 40px;
    font-weight: 700;
    padding: 8px 14px;
  }

  .dialog-submit-btn {
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
  }
`

const useAiAgentDialog = ({ onSubmit }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editingAgent, setEditingAgent] = useState(null)
  const [specialties, setSpecialties] = useState([])
  const [tierPrograms, setTierPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [aiVoices, setAiVoices] = useState([])
  const [linkedVoices, setLinkedVoices] = useState([])
  const [defaultVoiceId, setDefaultVoiceId] = useState(null)
  const [linkedSearch, setLinkedSearch] = useState("")
  const [voicesListOpen, setVoicesListOpen] = useState(false)
  const voicesListRef = useRef(null)
  const [linkedWasNull, setLinkedWasNull] = useState(false)
  const [tierProgramsLoading, setTierProgramsLoading] = useState(true)
  const [tierProgramsError, setTierProgramsError] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const voiceMatches = (v, q) => {
    if (!q) return true
    const hay = [v.name, v.provider_voice_id, v.provider, String(v.id), v.description].filter(Boolean).join(' ').toLowerCase()
    return hay.includes(q.toLowerCase())
  }
  const [voicesLoading, setVoicesLoading] = useState(false)
  const [llmCatalogModels, setLlmCatalogModels] = useState([])
  const [llmCatalogSource, setLlmCatalogSource] = useState({ openaiLive: true, anthropicLive: true })

  const loadAiVoices = async () => {
    setVoicesLoading(true)
    try {
      const resp = await listAiVoices({ page: 1, pageSize: 500 })
      const list = resp?.data?.list || resp?.list || resp || []
      setAiVoices(Array.isArray(list) ? list : [])
    } catch (e) {
      setAiVoices([])
    } finally {
      setVoicesLoading(false)
    }
  }

  const loadLlmCatalog = async () => {
    try {
      const raw = await listAvailableLlmModels()
      verifyResponse(raw)
      setLlmCatalogModels(mergeLlmCatalog(extractLlmCombinedFromResponse(raw)))
      setLlmCatalogSource(extractLlmSourceFromResponse(raw))
    } catch {
      setLlmCatalogModels(mergeLlmCatalog([]))
      setLlmCatalogSource({ openaiLive: false, anthropicLive: false })
    }
  }

  // Media Manager integration
  const [openMediaManager, MediaManagerContent] = useMediaManager()

  const openDialog = (agent = null) => {
    handleOpen(agent)
  }

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      title: "",
      shortDescription: "",
      longDescription: "",
      specialties: [],
      customInstructions: "",
      imageUrl: "",
      primaryColor: "#1976D2",
      backgroundColor: "#F5F5F5",
      sortOrder: 1,
      isActive: true,
      tierProgramId: null,
      productProgramId: null,
      voiceLocked: false,
      defaultVoice: "alloy",
      linkedAiVoiceId: '',
      studyLabPrimaryModel: '',
      studyLabFallbackModel: '',
    },
  })

  const watchedImageUrl = watch("imageUrl")
  const watchedStudyPrimary = watch("studyLabPrimaryModel")
  const watchedStudyFallback = watch("studyLabFallbackModel")
  const studyLabMenuModels = useMemo(() => {
    const map = new Map((llmCatalogModels || []).map((m) => [m.id, m]))
    ;[watchedStudyPrimary, watchedStudyFallback].forEach((raw) => {
      const id = (raw || "").trim()
      if (id && !map.has(id)) map.set(id, { id, provider: "saved" })
    })
    return Array.from(map.values())
  }, [llmCatalogModels, watchedStudyPrimary, watchedStudyFallback])

  // Load tier programs on component mount
  useEffect(() => {
    const loadTierPrograms = async () => {
      try {
        setTierProgramsLoading(true)
        setTierProgramsError(null)
        const response = await getTierPrograms()
        
        // Extract the data array from the response
        const programs = response.data || response
        
        // Ensure programs is an array
        if (Array.isArray(programs)) {
          setTierPrograms(programs)
        } else {
          throw new Error("Tier programs API returned non-array data")
        }

        // Load products (product gates) for "Product (select below)" tier
        try {
          const prod = await getProducts()
          setProducts(Array.isArray(prod) ? prod : [])
        } catch {
          setProducts([])
        }
      } catch (error) {
        setTierProgramsError(error.message)
        enqueueSnackbar("Failed to load tier programs", { variant: "error" })
      } finally {
        setTierProgramsLoading(false)
      }
    }
    
    loadTierPrograms()
    // load available voices for linking
    ;(async () => {
      try {
        const resp = await listAiVoices({ page: 1, pageSize: 500 })
        const list = resp?.data?.list || resp?.list || resp || []
        setAiVoices(Array.isArray(list) ? list : [])
      } catch (e) {
        setAiVoices([])
      }
    })()
  }, [enqueueSnackbar])

  // Ensure voices are loaded when the dialog is opened (or re-opened)
  useEffect(() => {
    if (!open) return
    let mounted = true
    ;(async () => {
      if (!mounted) return
      await loadAiVoices()
      await loadLlmCatalog()
    })()
    return () => { mounted = false }
  }, [open])

  // When the available voices finish loading, ensure linkedVoices and defaultVoiceId reflect the editingAgent
  useEffect(() => {
    if (!editingAgent) return
    if (!Array.isArray(aiVoices) || aiVoices.length === 0) return
    const existingLinkedIds = editingAgent.linkedAiVoiceIds && Array.isArray(editingAgent.linkedAiVoiceIds)
      ? editingAgent.linkedAiVoiceIds
      : (editingAgent.linkedAiVoiceId ? [editingAgent.linkedAiVoiceId] : [])
    let nextLinked = []
    if (!existingLinkedIds || existingLinkedIds.length === 0) {
      nextLinked = [...aiVoices]
      setLinkedWasNull(true)
    } else {
      nextLinked = aiVoices.filter(v => existingLinkedIds.includes(v.id))
      setLinkedWasNull(false)
    }
    setLinkedVoices(nextLinked)
    const defaultPvId = (editingAgent.defaultVoice || "").trim()
    const defaultVoice = nextLinked.find(v => (v.provider_voice_id || v.providerVoiceId || "").toString().trim() === defaultPvId)
    setDefaultVoiceId(defaultVoice ? defaultVoice.id : (nextLinked[0] ? nextLinked[0].id : null))
  }, [aiVoices, editingAgent])

  // Default tierProgramId to Free when opening create dialog (once tiers are loaded)
  useEffect(() => {
    if (!open) return
    if (editingAgent) return
    if (!Array.isArray(tierPrograms) || tierPrograms.length === 0) return
    try {
      const freeTier = tierPrograms.find((t) => String(t?.type || "").toLowerCase() === "free")
      if (freeTier?.id) {
        setValue("tierProgramId", Number(freeTier.id), { shouldDirty: true, shouldValidate: true })
        setValue("productProgramId", null, { shouldDirty: true, shouldValidate: true })
      }
    } catch {}
  }, [open, editingAgent, tierPrograms, setValue])

  // Close voices list when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (voicesListRef.current && !voicesListRef.current.contains(e.target)) {
        setVoicesListOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const availableSpecialties = [
    "Mindset",
    "Fitness",
    "Nutrition",
    "Wellness",
    "Productivity",
    "Relationships",
    "Career",
    "Finance",
    "All Specialties",
    "Multi-Domain Expertise",
    "Personalized Guidance",
    "Adaptive Learning",
  ]

  const handleOpen = (agent = null) => {
    setEditingAgent(agent)
    if (agent) {
      setValue("name", agent.name)
      setValue("title", agent.title)
      setValue("shortDescription", agent.shortDescription)
      setValue("longDescription", agent.longDescription)
      setValue("specialties", agent.specialties || [])
      setValue("customInstructions", agent.customInstructions)
      setValue("imageUrl", agent.imageUrl || "")
      setValue("primaryColor", agent.primaryColor || "#1976D2")
      setValue("backgroundColor", agent.backgroundColor || "#F5F5F5")
      setValue("sortOrder", agent.sortOrder)
      setValue("isActive", agent.isActive === 1)
      setValue("voiceLocked", agent.voiceLocked === 1)
      setValue("linkedAiVoiceId", agent.linkedAiVoiceId ? Number(agent.linkedAiVoiceId) : '')
      setValue("studyLabPrimaryModel", agent.studyLabPrimaryModel || "")
      setValue("studyLabFallbackModel", agent.studyLabFallbackModel || "")
      // linkedVoices and defaultVoiceId are set in useEffect when aiVoices load

      const tierProgramId =
        agent?.tierProgramId ??
        (agent?.tierProgram ? agent.tierProgram.id : null) ??
        null
      setValue("tierProgramId", tierProgramId ? Number(tierProgramId) : null)
      setValue("productProgramId", agent?.productProgramId ? Number(agent.productProgramId) : null)
      
      setSpecialties(agent.specialties || [])
    } else {
      reset()
      setSpecialties([])
      setLinkedVoices([])
      setDefaultVoiceId(null)
      setLinkedWasNull(false)
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingAgent(null)
    reset()
    setSpecialties([])
    setLinkedVoices([])
    setDefaultVoiceId(null)
    setLinkedWasNull(false)
  }

  const handleAddSpecialty = (specialty) => {
    if (!specialties.includes(specialty)) {
      setSpecialties([...specialties, specialty])
      setValue("specialties", [...specialties, specialty])
    }
  }

  const handleRemoveSpecialty = (specialtyToRemove) => {
    const newSpecialties = specialties.filter((s) => s !== specialtyToRemove)
    setSpecialties(newSpecialties)
    setValue("specialties", newSpecialties)
  }

  const handleAddCustomSpecialty = (customSpecialty) => {
    if (customSpecialty.trim() && !specialties.includes(customSpecialty.trim())) {
      const newSpecialties = [...specialties, customSpecialty.trim()]
      setSpecialties(newSpecialties)
      setValue("specialties", newSpecialties)
    }
  }

  // Handle media selection from MediaManager
  const handleMediaSelect = (selectedMedia) => {
    if (selectedMedia && selectedMedia.url) {
      setValue("imageUrl", selectedMedia.url)
      enqueueSnackbar("Image selected successfully", { variant: "success" })
    }
  }

  // Open media manager for image selection
  const handleOpenMediaManager = () => {
    openMediaManager(handleMediaSelect, [MEDIA_TYPE_INDEX.image], "search")
  }

  const onSubmitForm = async (data) => {
    setLoading(true)
    try {
      const resolvedDefaultVoice = (linkedVoices.find(v => v.id === defaultVoiceId) || linkedVoices[0])?.provider_voice_id || (linkedVoices[0]?.providerVoiceId)
      if (data.voiceLocked && (!linkedVoices.length || !resolvedDefaultVoice)) {
        enqueueSnackbar("Add at least one voice and set a default when Voice Lock is enabled", { variant: "error" })
        setLoading(false)
        return
      }

      const tierProgramId = data.tierProgramId ? Number(data.tierProgramId) : null
      if (!tierProgramId) {
        enqueueSnackbar("Tier Program is required", { variant: "error" })
        setLoading(false)
        return
      }
      const selectedTier = Array.isArray(tierPrograms)
        ? tierPrograms.find((t) => Number(t.id) === Number(tierProgramId))
        : null
      const isProductTier = selectedTier && String(selectedTier.type).toLowerCase() === "product"
      const productProgramId = data.productProgramId ? Number(data.productProgramId) : null
      if (isProductTier && !productProgramId) {
        enqueueSnackbar("Product is required when Tier Program is set to Product", { variant: "error" })
        setLoading(false)
        return
      }

      const submitData = {
        ...data,
        // Ensure numeric fields are properly typed
        sortOrder: Number(data.sortOrder),
        isActive: data.isActive ? 1 : 0,
        imageUrl: data.imageUrl || null,
        primaryColor: data.primaryColor || "#1976D2",
        backgroundColor: data.backgroundColor || "#F5F5F5",
        tierProgramId: tierProgramId,
        productProgramId: isProductTier ? productProgramId : null,
        voiceLocked: data.voiceLocked ? 1 : 0,
        defaultVoice: (resolvedDefaultVoice || "").trim() || null,
        linkedAiVoiceId: linkedWasNull ? null : (linkedVoices && linkedVoices.length > 0 ? Number(linkedVoices[0].id) : null),
        linkedAiVoiceIds: linkedWasNull ? (aiVoices || []).map(v => v.id) : (linkedVoices || []).map(v => v.id),
        studyLabPrimaryModel: (data.studyLabPrimaryModel || "").trim() || null,
        studyLabFallbackModel: (data.studyLabFallbackModel || "").trim() || null,
      }

      if (editingAgent) {
        submitData.id = editingAgent.id
        const response = await editAiAgent(submitData)
        verifyResponse(response)
        enqueueSnackbar("AI Agent updated successfully", { variant: "success" })
      } else {
        const response = await addAiAgent(submitData)
        verifyResponse(response)
        enqueueSnackbar("AI Agent created successfully", { variant: "success" })
      }

      handleClose()
      onSubmit()
    } catch (error) {
      const apiMessage = error?.response?.data?.message
      const message = Array.isArray(apiMessage)
        ? apiMessage.join(", ")
        : apiMessage || error?.message || "Error saving AI agent"
      enqueueSnackbar(message, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  return {
    open,
    loading,
    openDialog,
    closeDialog: handleClose,
    dialog: (
      <StyledAiAgentDialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">
              {editingAgent ? "Edit AI Agent" : "Add New AI Agent"}
            </h2>
            <p className="dialog-subtitle">
              Configure branding, specialties, access controls, and linked
              voice behavior.
            </p>
          </div>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Nutrition Expert"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Title"
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message}
                      placeholder="e.g., Certified Nutritionist"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="shortDescription"
                  control={control}
                  rules={{ required: "Short description is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Short Description"
                      fullWidth
                      multiline
                      rows={2}
                      error={!!errors.shortDescription}
                      helperText={errors.shortDescription?.message}
                      placeholder="Brief description of the AI agent"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="longDescription"
                  control={control}
                  rules={{ required: "Long description is required" }}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      label="Long Description"
                      placeholder="Detailed description of the AI agent's capabilities..."
                      toolbarConfig="description"
                      minHeight="150px"
                      error={!!errors.longDescription}
                      helperText={errors.longDescription?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Specialties
                </Typography>
                <Box mb={2}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {specialties.map((specialty, index) => (
                      <Chip
                        key={index}
                        label={specialty}
                        onDelete={() => handleRemoveSpecialty(specialty)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </div>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Quick Add:
                  </Typography>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "8px" }}>
                    {availableSpecialties
                      .filter((s) => !specialties.includes(s))
                      .map((specialty) => (
                        <Chip
                          key={specialty}
                          label={specialty}
                          size="small"
                          onClick={() => handleAddSpecialty(specialty)}
                          variant="outlined"
                          style={{ cursor: "pointer" }}
                        />
                      ))}
                  </div>
                </Box>
                <Controller
                  name="customInstructions"
                  control={control}
                  rules={{ required: "Custom instructions are required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Custom Instructions"
                      fullWidth
                      multiline
                      rows={6}
                      error={!!errors.customInstructions}
                      helperText={errors.customInstructions?.message}
                      placeholder="Detailed instructions for the AI agent's behavior and responses..."
                    />
                  )}
                />
              </Grid>
              
              {/* Image Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Agent Image
                </Typography>
                <Box mb={2}>
                  <Controller
                    name="imageUrl"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Selected Image URL"
                        fullWidth
                        placeholder="No image selected"
                        helperText="Image URL will be set automatically when you select media"
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    )}
                  />
                  <Box mt={1}>
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={handleOpenMediaManager}
                    >
                      Select Image from Media Library
                    </Button>
                  </Box>
                </Box>
                
                {/* Image Preview */}
                {watchedImageUrl && (
                  <Box mt={2} display="flex" justifyContent="center">
                    <img
                      src={watchedImageUrl}
                      alt="Agent Preview"
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid var(--cms-border)',
                        backgroundColor: 'var(--cms-surface-muted)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Controller
                  name="primaryColor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Primary Color"
                      fullWidth
                      type="color"
                      inputProps={{
                        style: { height: '56px' }
                      }}
                      helperText="Primary color for the agent's theme"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="backgroundColor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Background Color"
                      fullWidth
                      type="color"
                      InputProps={{
                        style: { height: '56px' }
                      }}
                      helperText="Background color for the agent's theme"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sortOrder"
                  control={control}
                  rules={{ required: "Sort order is required", min: { value: 0, message: "Sort order must be \u2265 0" } }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sort Order"
                      fullWidth
                      type="number"
                      error={!!errors.sortOrder}
                      helperText={errors.sortOrder?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="tierProgramId"
                  control={control}
                  rules={{ required: "Tier Program is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.tierProgramId || !!tierProgramsError}>
                      <InputLabel>Tier Program</InputLabel>
                      <Select 
                        {...field}
                        label="Tier Program"
                        disabled={tierProgramsLoading || !!tierProgramsError}
                        onChange={(e) => {
                          const nextId =
                            typeof e.target.value === "string"
                              ? Number(e.target.value)
                              : e.target.value
                          field.onChange(nextId)
                          const selectedTier = Array.isArray(tierPrograms)
                            ? tierPrograms.find((t) => Number(t.id) === Number(nextId))
                            : null
                          if (!selectedTier || String(selectedTier.type).toLowerCase() !== "product") {
                            setValue("productProgramId", null, { shouldDirty: true, shouldValidate: true })
                          }
                        }}
                      >
                        {tierProgramsLoading ? (
                          <MenuItem disabled>Loading tiers...</MenuItem>
                        ) : tierProgramsError ? (
                          <MenuItem disabled>Failed to load tiers</MenuItem>
                        ) : (
                          tierPrograms
                            .map((tier) => (
                              <MenuItem key={tier.id} value={tier.id}>
                                {String(tier.type).toLowerCase() === "product"
                                  ? "Product (select below)"
                                  : tier.type}
                              </MenuItem>
                            ))
                        )}
                      </Select>
                      {tierProgramsError && (
                        <FormHelperText error>
                          {tierProgramsError}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Product selector (only when Tier Program is Product) */}
              {(() => {
                const selectedTierId = watch("tierProgramId")
                  ? Number(watch("tierProgramId"))
                  : null
                const selectedTier = Array.isArray(tierPrograms)
                  ? tierPrograms.find((t) => Number(t.id) === Number(selectedTierId))
                  : null
                const isProductTier = selectedTier && String(selectedTier.type).toLowerCase() === "product"
                if (!isProductTier) return null
                return (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="productProgramId"
                      control={control}
                      rules={{ required: "Product is required when Tier Program is Product" }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.productProgramId}>
                          <InputLabel>Product</InputLabel>
                          <Select {...field} label="Product">
                            {Array.isArray(products) && products.length > 0 ? (
                              products.map((p) => (
                                <MenuItem key={p.programId} value={p.programId}>
                                  {p.name || p.code || `Product ${p.programId}`}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>No products available</MenuItem>
                            )}
                          </Select>
                          {errors.productProgramId && (
                            <FormHelperText error>
                              {errors.productProgramId?.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )
              })()}

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Study Lab model overrides
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                  Used when this agent matches <strong>STUDY_LAB_COACH_ID</strong>. Leave empty to use global Study Lab models.
                </Typography>
                {(!llmCatalogSource.openaiLive || !llmCatalogSource.anthropicLive) && (
                  <Alert severity="warning" variant="outlined" sx={{ mb: 1, py: 0.5 }}>
                    {!llmCatalogSource.openaiLive && !llmCatalogSource.anthropicLive
                      ? "Could not reach OpenAI or Anthropic APIs — showing hardcoded fallback model list. Check server API keys and network access."
                      : !llmCatalogSource.openaiLive
                      ? "Could not reach OpenAI API — OpenAI models are from the hardcoded fallback list."
                      : "Could not reach Anthropic API — Anthropic models are from the hardcoded fallback list."}
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="studyLabPrimaryModel"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="study-lab-primary-label">Study Lab primary</InputLabel>
                      <Select
                        labelId="study-lab-primary-label"
                        label="Study Lab primary"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Use global default</em>
                        </MenuItem>
                        {(studyLabMenuModels || []).map((m) => (
                          <MenuItem key={`p-${m.id}`} value={m.id}>
                            {m.provider}: {m.id}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Optional override from vendor catalog</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="studyLabFallbackModel"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="study-lab-fallback-label">Study Lab fallback</InputLabel>
                      <Select
                        labelId="study-lab-fallback-label"
                        label="Study Lab fallback"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Use global default</em>
                        </MenuItem>
                        {(studyLabMenuModels || []).map((m) => (
                          <MenuItem key={`f-${m.id}`} value={m.id}>
                            {m.provider}: {m.id}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Optional retry model</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Voice Settings */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="voiceLocked"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Lock Voice to this Agent"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel shrink>Voices</InputLabel>
                  <FormHelperText sx={{ mb: 1 }}>
                    Add voices for this agent and choose one as default. If Lock Voice is on, only the default is used.
                  </FormHelperText>
                  <Box ref={voicesListRef} sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Filter list (optional)"
                        fullWidth
                        value={linkedSearch}
                        onChange={(e) => setLinkedSearch(e.target.value)}
                        onFocus={() => setVoicesListOpen(true)}
                      />
                      <IconButton size="small" onClick={loadAiVoices} title="Reload voices">
                        <RefreshIcon />
                      </IconButton>
                    </Box>
                    {voicesListOpen && (
                    <Box sx={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 1300, mt: 0.5, maxHeight: 280, overflow: 'auto', border: '1px solid var(--cms-border)', borderRadius: 1, backgroundColor: 'var(--cms-dialog-bg)', boxShadow: '0 6px 12px rgba(0,0,0,0.1)' }}>
                      {voicesLoading ? (
                        <Box sx={{ px: 1, py: 1 }}><Typography color="textSecondary">Loading voices...</Typography></Box>
                      ) : (!Array.isArray(aiVoices) || aiVoices.length === 0) ? (
                        <Box sx={{ px: 1, py: 1 }}><Typography color="textSecondary">No voices available</Typography></Box>
                      ) : (
                        (() => {
                          const notYetLinked = (aiVoices || []).filter(v => !linkedVoices.some(l => l.id === v.id))
                          const matches = linkedSearch
                            ? notYetLinked.filter(v => voiceMatches(v, linkedSearch))
                            : notYetLinked
                          if (matches.length === 0) return (
                            <Box sx={{ px: 1, py: 1 }}>
                              <Typography color="textSecondary">
                                {linkedSearch ? 'No voices match your search.' : 'All voices are already added.'}
                              </Typography>
                            </Box>
                          )
                          return matches.slice(0, 50).map(v => (
                            <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' } }}>
                              <Typography sx={{ flex: '1 1 auto', minWidth: 0 }} noWrap>
                                {v.name || v.provider_voice_id || v.providerVoiceId || v.provider || v.id}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                {[v.tier, v.provider].filter(Boolean).map((tag) => (
                                  <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
                                ))}
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const next = [...linkedVoices, v]
                                    setLinkedVoices(next)
                                    if (linkedVoices.length === 0) setDefaultVoiceId(v.id)
                                    setLinkedSearch('')
                                  }}
                                >
                                  Add
                                </Button>
                              </Box>
                            </Box>
                          ))
                        })()
                      )}
                    </Box>
                    )}
                  </Box>

                  <Box mt={1}>
                    <Typography variant="caption" color="textSecondary">Selected voices (click to set as default):</Typography>
                    <Box mt={0.5} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {linkedVoices.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No voices selected. Add voices above; the first added is the default until you change it.</Typography>
                      ) : (
                        linkedVoices.map((v) => {
                          const name = v.name || v.provider_voice_id || v.providerVoiceId || v.provider || v.id
                          const tags = [v.tier, v.provider].filter(Boolean)
                          const tagsStr = tags.length ? ` (${tags.join(', ')})` : ''
                          const defaultSuffix = v.id === defaultVoiceId ? ' · default' : ''
                          return (
                          <Chip
                            key={v.id}
                            label={`${name}${tagsStr}${defaultSuffix}`}
                            onDelete={() => {
                              const next = linkedVoices.filter(x => x.id !== v.id)
                              setLinkedVoices(next)
                              if (v.id === defaultVoiceId) setDefaultVoiceId(next[0]?.id ?? null)
                            }}
                            color={v.id === defaultVoiceId ? 'primary' : 'default'}
                            variant={v.id === defaultVoiceId ? 'filled' : 'outlined'}
                            onClick={() => setDefaultVoiceId(v.id)}
                          />
                          )
                        })
                      )}
                    </Box>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              className="dialog-cancel-btn"
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className="dialog-submit-btn"
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? "Saving..." : editingAgent ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
        
        {/* Media Manager Dialog */}
        {MediaManagerContent}
      </StyledAiAgentDialog>
    ),
  }
}

export default useAiAgentDialog


